/**
 * loop_gate.ts — diagnose-then-mask gate for the oscillation failure class.
 *
 * Direct port of scratchbooks/_proto_loop_gate.py:LoopGate. The trained
 * GRU DRQN (2.66× random at 20×20) has one visible failure mode: it
 * gets stuck in a 4×4 / 4×6 basin, bouncing off a wall, a stone, or a
 * *believed* stone (a cell the shadow map wrongly flagged as STONE and
 * never re-queried). Telemetry on 30 baseline episodes shows ~29% of
 * all ticks are spent inside such a basin, with the median basin lasting
 * 19 ticks and the worst 72.
 *
 * The fix mirrors the diagnose-then-mask recipe from
 * scratchbooks/apps/portfolio/public/posts/mixture-of-bandits.md — same
 * discipline, different failure class:
 *
 *   1. Diagnose: oscillation (not bypass).
 *   2. Detect from regime-legitimate signals (no truth queries):
 *        speed  = ‖(σ_r, σ_c)‖  via closed-form 1-D OLS over a W-tick window
 *        gold_W = Σ reward > 0   over the same window
 *        is_loop ⟺ speed < τ_speed AND gold_W < τ_gold
 *   3. Switch off the directions the agent keeps picking (≥ k_hits in
 *      the window) and force a recency-based tie-break — pick the
 *      action whose destination cell was visited least recently.
 *      Argmax-of-rest doesn't work: masking one direction just makes
 *      the agent pick the opposite one and oscillate in a new pattern.
 *      The recency tie-break cannot oscillate by construction.
 *
 * The mask is PERSISTENT — it stays on until the agent demonstrates it
 * has escaped (speed ≥ τ_speed for `escape_burst` consecutive ticks, or
 * any gold event). A fixed cooldown lets the agent escape one basin,
 * expire, and get pulled straight back by the still-misleading shadow
 * belief. Persistence breaks that cycle.
 *
 * Composition with the existing `can_move` mask:
 *
 *     valid = can_move AND NOT loop_mask
 *     if not valid.any(): valid = can_move   // safety fallback
 *     if gate_active: a = argmin_recency(valid)
 *     else:           a = argmax(q[valid])
 *
 * Validated in Python at +4.2% gold collection on average (mean across
 * 8 base seeds × 30 layouts); best +18.8%, worst -4.1%. This is a
 * targeted intervention: it helps a lot when the agent is genuinely
 * stuck and can hurt a little when the agent is doing fine. See
 * _proto_loop_gate.py for the full validation table.
 */

import { N_ACTIONS, DIRECTIONS, GRID } from './environment';

export interface LoopGateOptions {
  /** Detector window size (ticks). 10 by default — the value that won
   *  the Python hyperparameter sweep. */
  window?: number;
  /** Loop threshold on net displacement per tick. Below this the agent
   *  is "barely moving". 0.25 ≈ bottom tercile of in-basin speeds. */
  tauSpeed?: number;
  /** Loop threshold on positive-reward sum over the window. Below this
   *  the agent is "collecting nothing". The discriminator that lets
   *  the gate stay silent when the agent is sweeping a real cluster. */
  tauGold?: number;
  /** Min action count in window to put on the mask when the gate fires.
   *  4 hits in a 10-tick window = "this direction is being repeatedly
   *  chosen". */
  kHits?: number;
  /** Consecutive escape ticks required to release the mask. */
  escapeBurst?: number;
  /** Recency map prune horizon (memory bound). */
  recencyHorizon?: number;
  /** Second-mask decay rate. When > 0, every cell within `tabooRadius`
   *  of a basin cell gets added to a taboo map at strength 1.0 on fire;
   *  strength decays by this rate per tick; revisits while strength >
   *  0.5 are masked. Validated as a VARIANCE REDUCER (10-seed eval):
   *  lifts worst-case from 0.94× to 0.99× at the cost of capping
   *  best-case from 1.19× to 1.12×. Default 0 = disabled. */
  tabooDecay?: number;
  /** Second-mask box radius around each basin cell. Default 2 = 5×5 box.
   *  Used only when `tabooDecay > 0`. */
  tabooRadius?: number;
}

export interface LoopGateDiagnostics {
  /** True when the detector currently thinks the agent is in a loop. */
  isLooping: boolean;
  /** True when the persistent mask is active (between fire and escape). */
  gateActive: boolean;
  /** Current ‖(σ_r, σ_c)‖ estimate. */
  speed: number;
  /** Current positive-reward sum over the window. */
  goldWindow: number;
  /** Cumulative fires this episode. */
  fireCount: number;
  /** Action indices currently masked (the loop-gate mask). */
  masked: ReadonlySet<number>;
  /** Cumulative actions blocked by the taboo mask this episode. */
  tabooBlocks: number;
}

/**
 * Diagnose-then-mask gate for the oscillation failure class.
 *
 * Usage:
 *   const gate = new LoopGate();
 *   gate.reset();
 *   gate.seedRecency(env.r, env.c);
 *   while (!env.done) {
 *     const obs = env.obs();
 *     const q = await agent.computeQ(obs);   // or however you get Q
 *     const canMove = canMoveMask(obs);
 *     const [loopMask, useTiebreak] = gate.preStep();
 *     let valid = andNot(canMove, loopMask);
 *     if (!any(valid)) valid = canMove;
 *     const a = useTiebreak
 *       ? gate.pickLeastRecentlyUsed(valid, env.r, env.c)
 *       : argmax(valid, q);
 *     const { reward } = env.step(a);
 *     gate.postStep(env.r, env.c, reward, a);
 *   }
 */
export class LoopGate {
  readonly window: number;
  readonly tauSpeed: number;
  readonly tauGold: number;
  readonly kHits: number;
  readonly escapeBurst: number;
  readonly recencyHorizon: number;
  readonly tabooDecay: number;
  readonly tabooRadius: number;

  // Rolling detector window.
  private rs: number[] = [];
  private cs: number[] = [];
  private rewards: number[] = [];
  private actions: number[] = [];

  // Persistent mask state.
  masked: Set<number> = new Set();
  private escapeProgress = 0;

  // Recency map: cell key "r,c" → tick last visited.
  private lastVisited: Map<string, number> = new Map();

  // Second-mask state: basin-cell taboo with per-tick decay. Same key
  // scheme as `lastVisited`. Strength in (0, 1]. When the gate fires,
  // every cell within `tabooRadius` of each loop-window cell is added
  // at strength 1.0; the strength decays by `tabooDecay` per tick. A
  // candidate action whose destination has strength > 0.5 is masked.
  // Empty map == disabled (decay=0).
  private taboo: Map<string, number> = new Map();
  tabooBlocks = 0;  // diagnostic: cumulative blocked actions

  // Diagnostics.
  isLooping = false;
  gateActive = false;
  speed = 0;
  goldWindow = 0;
  fireCount = 0;
  tick = 0;

  constructor(opts: LoopGateOptions = {}) {
    this.window = opts.window ?? 10;
    this.tauSpeed = opts.tauSpeed ?? 0.25;
    this.tauGold = opts.tauGold ?? 0.10;
    this.kHits = opts.kHits ?? 4;
    this.escapeBurst = opts.escapeBurst ?? 4;
    this.recencyHorizon = opts.recencyHorizon ?? 40;
    this.tabooDecay = opts.tabooDecay ?? 0;
    this.tabooRadius = opts.tabooRadius ?? 2;
  }

  /** Clear at the start of every episode. */
  reset(): void {
    this.rs = []; this.cs = [];
    this.rewards = []; this.actions = [];
    this.masked.clear();
    this.escapeProgress = 0;
    this.lastVisited.clear();
    this.taboo.clear();
    this.tabooBlocks = 0;
    this.isLooping = false;
    this.gateActive = false;
    this.speed = 0;
    this.goldWindow = 0;
    this.fireCount = 0;
    this.tick = 0;
  }

  /** Seed the recency map with the start cell (so the first tick has a
   *  sensible baseline; without this the agent's start cell looks
   *  "infinitely old" and the tie-breaker can favour returning to it). */
  seedRecency(r: number, c: number): void {
    this.lastVisited.set(`${r},${c}`, 0);
  }

  /** Snapshot of the gate's internal state — for the UI / HUD. */
  diagnostics(): LoopGateDiagnostics {
    return {
      isLooping: this.isLooping,
      gateActive: this.gateActive,
      speed: this.speed,
      goldWindow: this.goldWindow,
      fireCount: this.fireCount,
      masked: this.masked,
      tabooBlocks: this.tabooBlocks,
    };
  }

  /** Called BEFORE the runner picks the action this tick.
   *
   *  Returns `[loopMask, useTiebreak]`:
   *    loopMask    — set of action indices to ban this tick
   *    useTiebreak — if true, use `pickLeastRecentlyUsed` among valid
   *                  actions instead of argmax-of-rest
   *
   *  Note: the basin-cell taboo mask is queried separately via
   *  `tabooMaskForPose(r, c, valid)` — it operates on destination cells,
   *  not action indices, so it needs the agent's current pose.
   */
  preStep(): [Set<number>, boolean] {
    return [new Set(this.masked), this.gateActive];
  }

  /** Return the set of valid action indices whose destination cell is
   *  currently in the taboo set (strength > 0.5). Returns empty when
   *  the taboo map is empty (disabled). */
  tabooMaskForPose(r: number, c: number, valid: ArrayLike<number>): Set<number> {
    if (this.taboo.size === 0) return new Set();
    const banned = new Set<number>();
    for (let a = 0; a < N_ACTIONS; a++) {
      if (!valid[a]) continue;
      const [dr, dc] = DIRECTIONS[a];
      const key = `${r + dr},${c + dc}`;
      if ((this.taboo.get(key) ?? 0) > 0.5) banned.add(a);
    }
    if (banned.size > 0) this.tabooBlocks += banned.size;
    return banned;
  }

  /** Among `valid` actions, pick the one whose destination cell was
   *  visited LEAST recently. Unvisited cells get recency = -∞ (oldest).
   *  Returns -1 if no valid actions exist (the caller should fall back
   *  to plain argmax in that case).
   *
   *  `valid` is a numeric array (Uint8Array or number[]) where a
   *  truthy value means the action is allowed. We accept the numeric
   *  form because the runner's `valid` mask is a Uint8Array — accepting
   *  boolean[] would force a per-tick allocation. */
  pickLeastRecentlyUsed(valid: ArrayLike<number>, r: number, c: number): number {
    let bestA = -1;
    let bestRec = Infinity;
    for (let a = 0; a < N_ACTIONS; a++) {
      if (!valid[a]) continue;
      const [dr, dc] = DIRECTIONS[a];
      const key = `${r + dr},${c + dc}`;
      const rec = this.lastVisited.get(key) ?? -1;
      if (rec < bestRec) {
        bestRec = rec;
        bestA = a;
      }
    }
    return bestA;
  }

  /** Called AFTER env.step. Records the tick and recomputes state.
   *  Returns true iff the gate FIRED on this tick (transitioned from
   *  inactive to active). */
  postStep(r: number, c: number, reward: number, action: number): boolean {
    this.tick++;
    if (this.rs.length >= this.window) {
      this.rs.shift(); this.cs.shift();
      this.rewards.shift(); this.actions.shift();
    }
    this.rs.push(r); this.cs.push(c);
    this.rewards.push(reward); this.actions.push(action);
    this.lastVisited.set(`${r},${c}`, this.tick);

    // Prune stale recency entries to bound memory.
    if (this.lastVisited.size > 4 * this.recencyHorizon) {
      const cutoff = this.tick - this.recencyHorizon;
      for (const [k, v] of this.lastVisited) {
        if (v < cutoff) this.lastVisited.delete(k);
      }
    }

    // Need a full window before the detector can fire.
    if (this.rs.length < this.window) {
      this.isLooping = false;
      this.speed = 0;
      this.goldWindow = 0;
      return false;
    }

    // Closed-form 1-D OLS slopes: σ_v = cov(v, t) / var(t).
    const W = this.window;
    let tMean = 0;
    for (let t = 0; t < W; t++) tMean += t;
    tMean /= W;
    let tVar = 0;
    for (let t = 0; t < W; t++) tVar += (t - tMean) * (t - tMean);
    tVar /= W;

    if (tVar < 1e-9) {
      this.speed = 0;
    } else {
      let rMean = 0, cMean = 0;
      for (let i = 0; i < W; i++) { rMean += this.rs[i]; cMean += this.cs[i]; }
      rMean /= W; cMean /= W;
      let covR = 0, covC = 0;
      for (let i = 0; i < W; i++) {
        covR += (this.rs[i] - rMean) * (i - tMean);
        covC += (this.cs[i] - cMean) * (i - tMean);
      }
      covR /= W; covC /= W;
      const sr = covR / tVar;
      const sc = covC / tVar;
      this.speed = Math.hypot(sr, sc);
    }

    // Positive-reward sum over the window.
    let g = 0;
    for (let i = 0; i < W; i++) if (this.rewards[i] > 0) g += this.rewards[i];
    this.goldWindow = g;

    // Loop condition.
    this.isLooping = this.speed < this.tauSpeed && this.goldWindow < this.tauGold;

    let fired = false;
    if (this.isLooping && !this.gateActive) {
      // Compute mask: every action with ≥ kHits appearances in window.
      const counts = new Array<number>(N_ACTIONS).fill(0);
      for (let i = 0; i < W; i++) counts[this.actions[i]]++;
      this.masked.clear();
      for (let a = 0; a < N_ACTIONS; a++) {
        if (counts[a] >= this.kHits) this.masked.add(a);
      }
      // If everything got masked (kHits too low), fall back to the
      // top-2 most-used actions.
      if (this.masked.size >= N_ACTIONS - 1) {
        const indexed = counts.map((v, i) => [v, i] as const);
        indexed.sort((a, b) => b[0] - a[0]);
        this.masked.clear();
        this.masked.add(indexed[0][1]);
        this.masked.add(indexed[1][1]);
      }
      // Second mask: dump the basin's neighborhood into the taboo map
      // at full strength. Every cell within `tabooRadius` of each loop-
      // window cell gets strength 1.0; strength decays per tick and
      // blocks re-entry while > 0.5. Validated as a variance reducer
      // (lifts worst-case 0.94→0.99×, caps best-case 1.19→1.12×).
      if (this.tabooDecay > 0) {
        const R = this.tabooRadius;
        for (let i = 0; i < W; i++) {
          const br = this.rs[i];
          const bc = this.cs[i];
          for (let dr = -R; dr <= R; dr++) {
            for (let dc = -R; dc <= R; dc++) {
              const nr = br + dr;
              const nc = bc + dc;
              if (nr < 0 || nr >= GRID || nc < 0 || nc >= GRID) continue;
              this.taboo.set(`${nr},${nc}`, 1.0);
            }
          }
        }
      }
      this.gateActive = true;
      this.escapeProgress = 0;
      this.fireCount++;
      fired = true;
    }

    // Decay taboo strengths each tick. Drop entries below 0.01 so the
    // map doesn't grow unbounded.
    if (this.tabooDecay > 0 && this.taboo.size > 0) {
      const factor = 1.0 - this.tabooDecay;
      const decayed = new Map<string, number>();
      for (const [k, v] of this.taboo) {
        const v2 = v * factor;
        if (v2 > 0.01) decayed.set(k, v2);
      }
      this.taboo = decayed;
    }

    if (this.gateActive) {
      // Track escape progress: speed recovering OR any gold event.
      if (this.speed >= this.tauSpeed || reward > 0) {
        this.escapeProgress++;
      } else {
        this.escapeProgress = 0;
      }
      if (this.escapeProgress >= this.escapeBurst) {
        this.gateActive = false;
        this.masked.clear();
        this.escapeProgress = 0;
      }
    }

    return fired;
  }
}
